'use strict';

// Floating widgets (UsableNet): behaviours CSS cannot handle on its own.
// Styles live in scss/components/floating-widgets.

const WIDGET_SIZE = 48;
const WIDGET_GAP = 16;
const DEFAULT_BOTTOM = 24; // --fw-bottom default in the SCSS
const SCROLL_DELTA = 5; // px of scroll ignored, so tiny jitters do not toggle UsableNet

// Drawers lock the page (body fixed, scrollY 0) and restore the scroll position on close: those jumps
// are not the user scrolling. The lock is flagged by .no-zindex or an inline position: fixed on the body
function isPageLocked() {
  return document.body.classList.contains('no-zindex') || document.body.style.position === 'fixed';
}

const STICKY_CTA = '.add-to-cart-actions';

// PDP: keep the widgets above the add-to-cart bar while it covers their spot at the bottom of the viewport.
// The bar is fixed until its anchor, then absolute and scrolls with the page: both cases count, as long as
// it is inside the bottom band where the widgets sit; in-page copies (static, relative on desktop) never do.
function updateBottomOffset() {
  const band = window.innerHeight - (DEFAULT_BOTTOM + WIDGET_SIZE + WIDGET_GAP);
  let offset = 0;
  let sticky = false;
  document.querySelectorAll(STICKY_CTA).forEach(cta => {
    const position = getComputedStyle(cta).position;
    if (position !== 'fixed' && position !== 'absolute') return;
    sticky = true;
    const rect = cta.getBoundingClientRect();
    // fixed to the bottom: its own height. Measuring it against innerHeight breaks on iOS, where the toolbar
    // animation moves the bar a few frames before innerHeight changes and the widgets would bounce after it
    if (position === 'fixed' && getComputedStyle(cta).bottom === '0px') {
      offset = Math.max(offset, rect.height);
    } else if (rect.height && rect.top < window.innerHeight && rect.bottom > band) {
      offset = Math.max(offset, window.innerHeight - rect.top);
    }
  });
  // with a sticky bar on the page the SCSS starts at the bar offset (mobile PDP), so the default must be explicit
  let bottom = '';
  if (offset) {
    bottom = offset + WIDGET_GAP + 'px';
  } else if (sticky) {
    bottom = DEFAULT_BOTTOM + 'px';
  }
  if (document.body.style.getPropertyValue('--fw-bottom') !== bottom) {
    if (bottom) {
      document.body.style.setProperty('--fw-bottom', bottom);
    } else {
      document.body.style.removeProperty('--fw-bottom');
    }
  }
}

module.exports = {
  init: function () {
    let lastScrollY = window.scrollY;
    let wasLocked = false;
    let frame;

    const refresh = () => {
      frame = null;
      const locked = isPageLocked();
      // drawers flag the page in two ways (.no-zindex or a fixed body): one class hides UsableNet for both
      document.body.classList.toggle('fw-locked', locked);
      // while a drawer is open the add-to-cart bar can leave its fixed/absolute state: keep the last position
      if (locked) {
        wasLocked = true;
        return;
      }
      if (wasLocked) {
        // drawer just closed: jump straight to the right spot (no bottom transition), the fade-in still runs
        wasLocked = false;
        document.body.classList.add('fw-instant');
        updateBottomOffset();
        requestAnimationFrame(() => requestAnimationFrame(() => document.body.classList.remove('fw-instant')));
        return;
      }
      updateBottomOffset();
    };
    const scheduleRefresh = () => {
      if (!frame) frame = requestAnimationFrame(refresh);
    };

    window.addEventListener('scroll', () => {
      // UsableNet (Figma): fades out while scrolling down, back in on scroll up or at the top of the page
      const y = window.scrollY;
      // while locked (the position restore on close happens before the unlock) only move the reference point
      if (isPageLocked()) {
        lastScrollY = y;
      } else if (y <= 0) {
        document.body.classList.remove('fw-scrolled-down');
        lastScrollY = y;
      } else if (Math.abs(y - lastScrollY) >= SCROLL_DELTA) {
        document.body.classList.toggle('fw-scrolled-down', y > lastScrollY);
        lastScrollY = y;
      }
      scheduleRefresh();
    }, { passive: true });

    window.addEventListener('resize', scheduleRefresh);

    // drawers open/close by changing the body class or inline style, often without any scroll: recompute then
    new MutationObserver(scheduleRefresh).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });

    refresh();
  }
};
